import type {
  HypercoreUserPositionsOptions,
  HypercoreVaultPosition,
} from './types'

/** Hyperliquid info endpoint (read-only, no signing). */
const DEFAULT_INFO_URL = 'https://api.hyperliquid.xyz/info'

interface UserVaultEquity {
  vaultAddress: string
  equity: string
}

interface VaultFollower {
  user: string
  vaultEquity?: string
  pnl?: string
  lockupUntil?: number // milliseconds
}

interface VaultDetailsResponse {
  followers?: VaultFollower[]
  maxWithdrawable?: string
}

const postInfo = async <T>(url: string, body: unknown): Promise<T> => {
  const res = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`Hyperliquid info ${res.status}`)
  return (await res.json()) as T
}

/**
 * Read a user's HyperCore vault positions from the Hyperliquid L1 info
 * API and normalise them to {@link HypercoreVaultPosition}.
 *
 * Two-stage:
 *  1. `userVaultEquities` → one call listing every vault the user holds
 *     equity in (`{ vaultAddress, equity }`).
 *  2. (optional, default on) per-vault `vaultDetails` to read the
 *     caller's `followers[]` entry for `lockupUntil` (ms → seconds),
 *     `pnl`, and the vault's `maxWithdrawable`. Run in parallel.
 *
 * Off-chain and unproven on-chain — amounts are decimal USD strings as
 * returned by the L1. Per-vault `vaultDetails` failures degrade to a
 * position without lockup info rather than sinking the whole call.
 */
export const getHypercoreUserPositions = async (
  user: string,
  options?: HypercoreUserPositionsOptions,
): Promise<HypercoreVaultPosition[]> => {
  const apiUrl = options?.apiUrl ?? DEFAULT_INFO_URL
  const withLockup = options?.withLockup ?? true
  const now = options?.nowSeconds ?? Math.floor(Date.now() / 1000)
  const lcUser = user.toLowerCase()

  const equities = await postInfo<UserVaultEquity[]>(apiUrl, {
    type: 'userVaultEquities',
    user,
  })
  if (!Array.isArray(equities) || equities.length === 0) return []

  const base: HypercoreVaultPosition[] = equities
    .filter((e) => e?.vaultAddress)
    .map((e) => ({
      vault: e.vaultAddress.toLowerCase(),
      equity: e.equity ?? '0',
    }))

  if (!withLockup) return base

  return Promise.all(
    base.map(async (pos) => {
      try {
        const details = await postInfo<VaultDetailsResponse>(apiUrl, {
          type: 'vaultDetails',
          vaultAddress: pos.vault,
          user,
        })
        const follower = details.followers?.find(
          (f) => f.user?.toLowerCase() === lcUser,
        )
        if (!follower) return pos
        const lockupUntil =
          typeof follower.lockupUntil === 'number'
            ? Math.floor(follower.lockupUntil / 1000)
            : undefined
        return {
          ...pos,
          lockupUntil,
          lockStatus:
            lockupUntil === undefined
              ? undefined
              : now >= lockupUntil
                ? 'withdrawable'
                : 'locked',
          maxWithdrawable: details.maxWithdrawable,
          pnl: follower.pnl,
        }
      } catch {
        // Lockup enrichment is best-effort — keep the equity-only entry.
        return pos
      }
    }),
  )
}
